<template>
  <section class="filters" data-cy="product-filters-menu">
    <div class="filters__actions">
      <input
        type="button"
        value="Clear"
        class="filter-button-clear"
        @click.prevent="clearFilters"
        data-cy="clear-filters-button"
      />
      <input
        type="button"
        value="Apply"
        class="filter-button-apply"
        @click.prevent="applyFilters"
        data-cy="apply-filters-button"
      />
    </div>
    <ul class="filters__list">
      <li v-for="item in filters" :key="item.label" data-cy="filters-item">
        <label data-cy="filters-item-label">{{ item.label }}</label>
        <DropDownList
          class="filters-dropdown"
          :data-items="item.values"
          v-model="item.value"
          :loading="item.loading"
          data-cy="filters-item-dropdown"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import { onBeforeMount, ref, watch } from "vue";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import moment from "moment";

const props = defineProps<{
  filters: FilterMenuItem[];
  getPropValues: (field: string) => Promise<SubmissionsTable[]>;
}>();

const emits = defineEmits<{
  (event: "apply", filters: FilterMenuItem[]): void;
  (event: "clear"): void;
  (event: "close"): void;
}>();

const filters = ref([
  {
    field: "JobId",
    label: "Job ID",
    placeholder: "Select a Job ID",
    values: [] as number[],
    value: "Select a Job ID",
  },
  {
    field: "JobType",
    label: "Job Type",
    placeholder: "Select a Job Type",
    values: [] as string[],
    value: "Select a Job Type",
  },
  {
    field: "UserLogin",
    label: "Submitted By",
    placeholder: "Select a value",
    values: [] as string[],
    value: "Select a value",
  },
  {
    field: "DateProcessedForSort",
    label: "Date",
    placeholder: "Select a Date",
    values: [] as string[],
    value: "Select a Date",
    getFilterValue: (value: string) => moment(value).format("YYYY-MM-DD"),
  },
  {
    field: "Status",
    label: "Status",
    placeholder: "Select a Status",
    values: [] as string[],
    value: "Select a Status",
  },
] as FilterMenuItem[]);

const setFilterValues = () => {
  props.filters.forEach((f) => {
    const filter = filters.value.find((item) => item.field === f.field);
    if (filter) {
      filter.value = f.value;
    }
  });
};

setFilterValues();

const applyFilters = () => {
  const setFilters = filters.value.filter((f) => f.value !== f.placeholder);

  emits("apply", setFilters);
  emits("close");
};

const clearFilters = () => {
  filters.value.forEach((f) => {
    f.value = f.placeholder;
  });
  emits("apply", []);
  emits("close");
};

const updateFilterValuesAsync = async (filter: FilterMenuItem) => {
  try {
    filter.loading = true;
    const values = await props.getPropValues(filter.field);
    filter.values = values.map((item) => (item as any)[filter.field]);
  } finally {
    filter.loading = false;
  }
};

onBeforeMount(async () => {
  for (const filter of filters.value.filter((f) => f.values.length === 0)) {
    updateFilterValuesAsync(filter);
  }
});

watch(
  () => props.filters,
  () => {
    setFilterValues();
  }
);
</script>

<style lang="scss">
.filters {
  position: absolute;
  background: #fff;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  padding: 20px;
  overflow: auto;
  display: block;
  z-index: 100;
  height: 500px;

  &__actions {
    padding: 15px;
    border-bottom: 1px solid #ddd;
    margin: -20px -20px 10px;
    background: #efefef;
    justify-content: flex-end;
    display: flex;

    .filter-button-clear {
      text-align: center;
      padding: 8px 20px;
      box-shadow: none;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      background: 0 0;
      color: #3a5da6 !important;
      display: block;
      margin: 0 5px;
    }

    .filter-button-apply {
      text-align: center;
      color: #fff !important;
      padding: 8px 20px;
      box-shadow: none;
      background: #3a5da6;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      display: block;
      margin: 0 5px;
    }
  }

  &__list {
    padding-left: 0;

    li {
      list-style-type: none;
    }

    label {
      font-weight: 500;
      font-size: 11px;
      text-transform: uppercase;
      color: #999;
      margin-bottom: 5px;
      display: inline-block;
    }

    .filters-dropdown {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}
</style>
